// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Flex, Skeleton, Text } from '@mantine/core';
import { FC, useContext, useEffect, useState } from 'react';
import { IconCalendarEvent } from '@tabler/icons';
import LocationDistance from '../LocationDistance';
import { openEditContactModal } from '../../../Organismns/Contact/EditContactModal';
import { Contact, ContactS } from '../../../Service/ContactS';
import { RecommendationDto } from '../../../Types/RecommendationT';
import { EventContext } from '../../../Service/Context/EventContext';
import { EventDateDTO } from '../../../Types/MessageT';
import { EventS } from '../../../Service/EventS';
import { StringUtils } from '../../../Utils/utils';
import GrayBox from '../../../Atoms/GrayBox';
import ExportEventInfoStatus from './ExportEventInfoStatus';
import RecommendationStateStatus from './RecommendationState';
import RecommendationAnswerStatus from './RecommendationAnswerStatus';
import RecommendationActions from './RecommendationActions';

const RecommendationRequest: FC<{ recommendation: RecommendationDto }> = ({ recommendation }) => {
  const [contact, setContact] = useState<Contact>();
  const [loadingContact, setLoadingContact] = useState(false);
  const [availabilityConfirmedFromExternalSource, setAvailabilityConfirmed] = useState(false);
  const { event } = useContext<any>(EventContext);
  const { dates } = event;
  const { recommendedContactId, answer, eventDatesIds } = recommendation;

  const selectedDates = dates
    .filter((d: EventDateDTO) => eventDatesIds.includes(d.id))
    .map((d: EventDateDTO) => EventS.formatEventDate(new Date(d.date)));

  useEffect(() => {
    setLoadingContact(true);
    ContactS.fetchById(recommendedContactId)
      .then((_contact) => _contact && setContact(_contact))
      .finally(() => setLoadingContact(false));
  }, [recommendedContactId]);

  if (loadingContact) {
    return <Skeleton width="100%" height={100} />;
  }
  if (!contact) {
    return null;
  }

  const { personDescription, email } = contact;
  const showLocationInfo = answer === 'UNKNOWN' || answer === 'AVAILABLE';
  const showExportInfo =
    email && (answer === 'AVAILABLE' || (availabilityConfirmedFromExternalSource && answer === 'UNKNOWN'));

  return (
    <GrayBox p={0}>
      <Box p="sm">
        <Flex mb="xs">
          <Text
            weight="bolder"
            onClick={() => openEditContactModal(contact, undefined, setContact)}
            sx={{ cursor: 'pointer', flexGrow: 1 }}
            className="overflow-ellipsis"
          >
            {personDescription ?? email}
          </Text>
          <RecommendationActions recommendation={recommendation} contactEmail={email} contact={contact} />
        </Flex>
        {showLocationInfo && <LocationDistance contactId={recommendedContactId} />}

        <RecommendationAnswerStatus
          recommendation={recommendation}
          setAvailabilityConfirmed={setAvailabilityConfirmed}
        />
        {showExportInfo && (
          <>
            <Box
              ml={18}
              sx={(theme) => ({
                width: 5,
                height: 5,
                backgroundColor: theme.colorScheme !== 'dark' ? 'white' : 'black',
              })}
            />
            <ExportEventInfoStatus recommendation={recommendation} contact={contact} />
          </>
        )}
        <Box
          ml={18}
          sx={(theme) => ({
            width: 5,
            height: 5,
            backgroundColor: theme.colorScheme !== 'dark' ? 'white' : 'black',
          })}
        />
        <RecommendationStateStatus recommendation={recommendation} contactEmail={email} recommendedContact={contact} />
      </Box>
      {selectedDates.length > 0 && (
        <Text
          p={3}
          size="xs"
          sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.blue[1] : theme.colors.blue[4],
            color: 'white',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            width: '100%',
          })}
          className="overflow-ellipsis"
        >
          <IconCalendarEvent size={16} style={{ marginRight: 5, marginLeft: 5, marginTop: -4 }} />{' '}
          {StringUtils.concatStrings(selectedDates)}
        </Text>
      )}
    </GrayBox>
  );
};

export default RecommendationRequest;
